import type { ObjectComparisonObject } from './object-comparison.js';

export const MARKET_SEARCH_RADII = [1, 3, 5, 10] as const;
export type MarketSearchRadius = (typeof MARKET_SEARCH_RADII)[number];

export const MARKET_PROPERTY_TYPES = ['apartment', 'house', 'land'] as const;
export type MarketPropertyType = (typeof MARKET_PROPERTY_TYPES)[number];

export const MARKET_TRANSACTIONS = ['purchase', 'rent'] as const;
export type MarketTransaction = (typeof MARKET_TRANSACTIONS)[number];

export const SWISS_CANTONS = [
  ['AG', 'Aargau'], ['AI', 'Appenzell Innerrhoden'], ['AR', 'Appenzell Ausserrhoden'], ['BE', 'Bern'],
  ['BL', 'Basel-Landschaft'], ['BS', 'Basel-Stadt'], ['FR', 'Freiburg'], ['GE', 'Genf'], ['GL', 'Glarus'],
  ['GR', 'Graubünden'], ['JU', 'Jura'], ['LU', 'Luzern'], ['NE', 'Neuenburg'], ['NW', 'Nidwalden'],
  ['OW', 'Obwalden'], ['SG', 'St. Gallen'], ['SH', 'Schaffhausen'], ['SO', 'Solothurn'], ['SZ', 'Schwyz'],
  ['TG', 'Thurgau'], ['TI', 'Tessin'], ['UR', 'Uri'], ['VD', 'Waadt'], ['VS', 'Wallis'], ['ZG', 'Zug'], ['ZH', 'Zürich'],
] as const;

export interface MarketPriceComparisonInput {
  location: unknown;
  canton: unknown;
  radius: unknown;
  propertyType: unknown;
  transaction: unknown;
  rooms?: unknown;
  livingArea?: unknown;
  minPrice?: unknown;
  maxPrice?: unknown;
  yearBuilt?: unknown;
  condition?: unknown;
}

export interface MarketPriceComparisonCriteria {
  location: string;
  canton: string;
  radius: MarketSearchRadius;
  propertyType: MarketPropertyType;
  transaction: MarketTransaction;
  rooms: number | null;
  livingArea: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  yearBuilt: number | null;
  condition: string;
}

export interface MarketSearchSource {
  id: 'homegate' | 'comparis' | 'immoscout24';
  name: string;
  url: string;
  external: true;
  parameterSupport: 'regional-entry';
  note: string;
}

export interface MarketInternalObject extends ObjectComparisonObject {
  comparisonId: string;
  comparisonName: string;
  comparisonUpdatedAt: string;
}

export const MARKET_PROPERTY_TYPE_LABELS: Record<MarketPropertyType, string> = {
  apartment: 'Wohnung',
  house: 'Haus',
  land: 'Grundstück',
};

export const MARKET_TRANSACTION_LABELS: Record<MarketTransaction, string> = {
  purchase: 'Kauf',
  rent: 'Miete',
};

function textValue(value: unknown, key: string, maxLength: number, required = false): string {
  if (value === null || value === undefined) {
    if (required) throw new Error(`Bitte gib ${key} an.`);
    return '';
  }
  if (typeof value !== 'string') throw new Error(`${key} muss ein Text sein.`);
  const normalized = value.trim();
  if (required && !normalized) throw new Error(`Bitte gib ${key} an.`);
  if (normalized.length > maxLength) throw new Error(`${key} ist zu lang.`);
  return normalized;
}

export function parseSwissMarketNumber(value: unknown, key: string): number | null {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return null;
  if (typeof value !== 'number' && typeof value !== 'string') throw new Error(`${key} muss eine Zahl sein oder leer bleiben.`);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error(`${key} enthält keine gültige Zahl.`);
    return value;
  }
  let normalized = value.trim().replace(/[\u0027\u2019\u2018\u0060\s]/g, '');
  if (!normalized) return null;
  if ((normalized.match(/,/g) || []).length > 1) throw new Error(`${key} enthält zu viele Dezimaltrennzeichen.`);
  if (normalized.includes(',')) {
    if (normalized.includes('.')) throw new Error(`${key} verwendet ein uneindeutiges Zahlenformat.`);
    normalized = normalized.replace(',', '.');
  }
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) throw new Error(`${key} verwendet ein nicht unterstütztes Zahlenformat.`);
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) throw new Error(`${key} enthält keine gültige Zahl.`);
  return parsed;
}

function boundedNumber(value: unknown, key: string, min: number, max: number): number | null {
  const parsed = parseSwissMarketNumber(value, key);
  if (parsed === null) return null;
  if (parsed < min || parsed > max) throw new Error(`${key} liegt ausserhalb des plausiblen Bereichs.`);
  return parsed;
}

function enumValue<T extends readonly string[]>(value: unknown, key: string, allowed: T): T[number] {
  if (typeof value !== 'string' || !allowed.includes(value)) throw new Error(`${key} ist ungültig.`);
  return value as T[number];
}

export function normalizeMarketPriceComparisonCriteria(input: unknown): MarketPriceComparisonCriteria {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Die Marktsuche konnte nicht gelesen werden.');
  const raw = input as MarketPriceComparisonInput;
  const location = textValue(raw.location, 'Ort oder Adresse', 160, true);
  const canton = enumValue(raw.canton, 'Kanton', SWISS_CANTONS.map(([code]) => code) as unknown as readonly string[]);
  const radius = Number(enumValue(String(raw.radius ?? ''), 'Suchradius', MARKET_SEARCH_RADII.map(String) as unknown as readonly string[])) as MarketSearchRadius;
  const propertyType = enumValue(raw.propertyType, 'Objektart', MARKET_PROPERTY_TYPES);
  const transaction = enumValue(raw.transaction, 'Vermarktungsart', MARKET_TRANSACTIONS);
  const rooms = boundedNumber(raw.rooms, 'Zimmer', 0, 100);
  const livingArea = boundedNumber(raw.livingArea, 'Wohnfläche', 0, 1_000_000);
  const minPrice = boundedNumber(raw.minPrice, 'Preis von', 0, 2_000_000_000);
  const maxPrice = boundedNumber(raw.maxPrice, 'Preis bis', 0, 2_000_000_000);
  if (minPrice !== null && maxPrice !== null && minPrice > maxPrice) throw new Error('«Preis von» darf nicht grösser als «Preis bis» sein.');
  const yearBuilt = boundedNumber(raw.yearBuilt, 'Baujahr', 1500, new Date().getFullYear() + 2);
  const condition = textValue(raw.condition, 'Zustand', 120);
  return { location, canton, radius, propertyType, transaction, rooms, livingArea, minPrice, maxPrice, yearBuilt: yearBuilt === null ? null : Math.round(yearBuilt), condition };
}

function regionalNote(source: string): string {
  return `${source} öffnet eine öffentliche regionale Suche. Filter werden nicht automatisch übernommen; übertrage die Kriterien aus der Zusammenfassung und prüfe sie dort.`;
}

export function buildMarketSearchSources(criteria: MarketPriceComparisonCriteria): MarketSearchSource[] {
  const transactionPath = criteria.transaction === 'rent' ? 'mieten' : 'kaufen';
  return [
    {
      id: 'homegate',
      name: 'Homegate',
      url: 'https://www.homegate.ch/c/de/immobilien/immobilien-suchen',
      external: true,
      parameterSupport: 'regional-entry',
      note: regionalNote('Homegate'),
    },
    {
      id: 'comparis',
      name: 'Comparis',
      url: 'https://www.comparis.ch/immobilien/portal/list?origin=nav',
      external: true,
      parameterSupport: 'regional-entry',
      note: regionalNote('Comparis'),
    },
    {
      id: 'immoscout24',
      name: 'ImmoScout24',
      url: `https://www.immoscout24.ch/de/immobilien/${transactionPath}`,
      external: true,
      parameterSupport: 'regional-entry',
      note: regionalNote('ImmoScout24'),
    },
  ];
}

export function marketCriteriaSummary(criteria: MarketPriceComparisonCriteria): string[] {
  return [
    `${criteria.transaction === 'rent' ? 'Miete' : 'Kauf'} · ${MARKET_PROPERTY_TYPE_LABELS[criteria.propertyType]}`,
    `${criteria.location}, ${criteria.canton} · Radius ${criteria.radius} km`,
    criteria.rooms === null ? 'Zimmer: unbekannt' : `Zimmer: ${criteria.rooms}`,
    criteria.livingArea === null ? 'Wohnfläche: unbekannt' : `Wohnfläche: ${criteria.livingArea} m²`,
    criteria.minPrice === null && criteria.maxPrice === null ? 'Preisrahmen: unbekannt' : `Preisrahmen: ${criteria.minPrice ?? 'offen'}–${criteria.maxPrice ?? 'offen'} CHF`,
    criteria.yearBuilt === null ? 'Baujahr: unbekannt' : `Baujahr: ${criteria.yearBuilt}`,
    criteria.condition ? `Zustand: ${criteria.condition}` : 'Zustand: unbekannt',
  ];
}
